import { useEffect } from 'react';
import { BRAND } from '../lib/brand';

interface DocumentMetaOptions {
  title: string;
  description: string;
  /** Route path for canonical/og:url, e.g. '/blog/some-post'. Defaults to the current path. */
  path?: string;
  /** Absolute image URL for og:image/twitter:image. Defaults to the site-wide OG image. */
  image?: string;
  /** Structured data (schema.org) for this page, e.g. Organization/SoftwareApplication. Omit for pages with none. */
  jsonLd?: object;
}

const DEFAULT_OG_IMAGE = `${BRAND.canonicalUrl}/og-image.png`;

const setMetaTag = (attr: 'name' | 'property', key: string, content: string) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href: string) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

// Structured data is page-specific (a blog post shouldn't carry the homepage's
// Organization/SoftwareApplication graph), so this removes any previous
// page's script before adding this page's - not just overwriting it.
const setJsonLd = (jsonLd: object | undefined) => {
  const existing = document.querySelector('script[data-managed-jsonld]');
  if (!jsonLd) {
    existing?.remove();
    return;
  }
  const el = existing ?? document.createElement('script');
  el.setAttribute('type', 'application/ld+json');
  el.setAttribute('data-managed-jsonld', 'true');
  el.textContent = JSON.stringify(jsonLd);
  if (!existing) document.head.appendChild(el);
};

/**
 * index.html ships one static <title>/description, so without this every
 * route shows the same "Cyber Garage" tags. Mutating <head> on mount fixes
 * what Googlebot indexes (it renders JS before reading <head>) but NOT
 * link-preview bots (LinkedIn, Twitter, Slack) - those fetch raw HTML and
 * never run this. Fixing those needs prerendering/SSR, a separate project.
 */
export const useDocumentMeta = ({ title, description, path, image, jsonLd }: DocumentMetaOptions) => {
  useEffect(() => {
    const fullTitle = `${title} | ${BRAND.productName}`;
    const url = `${BRAND.canonicalUrl}${path ?? window.location.pathname}`;
    const imageUrl = image ?? DEFAULT_OG_IMAGE;

    document.title = fullTitle;
    setMetaTag('name', 'description', description);
    setMetaTag('property', 'og:title', fullTitle);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', url);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:image', imageUrl);
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', fullTitle);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', imageUrl);
    setCanonical(url);
    setJsonLd(jsonLd);
  }, [title, description, path, image, jsonLd]);
};
